import * as React from "react";
import { useEffect, useState } from "react";
import { Box, Typography, Button, useMediaQuery, useTheme, IconButton, InputAdornment } from "@mui/material";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import NewCard from "../../app/components/NewCard";
import { centered_flex_box, main_button, MainInput } from "../../app/components/Styles";
import { Table } from "antd";
import { Search } from '@mui/icons-material';
import { getProductsSimplified, updateReturnsSystemProducts } from "../../app/store/actions/dataActions";
import { useTranslation } from "react-i18next";

const ManageProducts = ({ getProductsSimplified, updateReturnsSystemProducts, products, isLoading }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const [productFlags, setProductFlags] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);

  useEffect(() => {
    getProductsSimplified();
  }, []);

  useEffect(() => {
    if (products && Array.isArray(products)) {
      const mappedProducts = products.map((p) => ({
        product: p._id,
        name: p.name,
        sku: p.sku || p.externalId,
        isReturnable: p.isReturnable !== undefined ? !!p.isReturnable : true,
        isExchangable: p.isExchangable !== undefined ? !!p.isExchangable : true,
      }));
      setProductFlags(mappedProducts);
      setFilteredProducts(mappedProducts);
    } else if (products?.docs && Array.isArray(products.docs)) {
      const mappedProducts = products.docs.map((p) => ({
        product: p._id,
        name: p.name,
        sku: p.sku || p.externalId,
        isReturnable: p.isReturnable !== undefined ? !!p.isReturnable : true,
        isExchangable: p.isExchangable !== undefined ? !!p.isExchangable : true,
      }));
      setProductFlags(mappedProducts);
      setFilteredProducts(mappedProducts);
    }
  }, [products]);

  const handleSaveProducts = () => {
    const productUpdates = productFlags.map(({ product, isReturnable, isExchangable }) => ({ product, isReturnable, isExchangable }));
    updateReturnsSystemProducts({ productUpdates });
  };

  const handleSearch = () => {
    if (!search.trim()) {
      setFilteredProducts(productFlags);
      return;
    }
    
    const filtered = productFlags.filter((product) => 
      product.name.toLowerCase().includes(search.toLowerCase()) ||
      product.sku.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredProducts(filtered);
  };

  const handleClearSearch = () => {
    setSearch('');
    setFilteredProducts(productFlags);
  };

  // Table renders filteredProducts; keep both lists in sync when toggling flags
  const toggleProductFlag = (productId, flag) => {
    const apply = (list) =>
      list.map((x) => (x.product === productId ? { ...x, [flag]: !x[flag] } : x));
    setProductFlags(apply);
    setFilteredProducts(apply);
  };

  const flagButtonSx = (active) => ({
    ...main_button,
    bgcolor: active ? 'var(--primaryColor)' : 'var(--secColor)',
    background: active ? 'var(--primaryColor)' : 'var(--secColor)',
    boxShadow: 'none',
    textTransform: 'none',
    py: 0.5,
    px: 1.5,
    minWidth: 56,
    fontSize: '0.75rem',
    ':hover': {
      bgcolor: active ? 'var(--primaryColor)' : 'var(--secColor)',
      background: active ? 'var(--primaryColor)' : 'var(--secColor)',
      opacity: 0.9,
    },
  });

  const columns = [
    { title: t("common.sku"), dataIndex: 'sku', key: 'sku' },
    { title: t("common.name"), dataIndex: 'name', key: 'name' },
    {
      title: t("returnsSystem.manageProducts.exchangeable"), key: 'isExchangable',
      render: (_, record) => (
        <Button
          size="small"
          sx={flagButtonSx(record.isExchangable)}
          onClick={() => toggleProductFlag(record.product, 'isExchangable')}
        >
          {record.isExchangable ? t("common.yes") : t("common.no")}
        </Button>
      )
    },
    {
      title: t("returnsSystem.manageProducts.returnable"), key: 'isReturnable',
      render: (_, record) => (
        <Button
          size="small"
          sx={flagButtonSx(record.isReturnable)}
          onClick={() => toggleProductFlag(record.product, 'isReturnable')}
        >
          {record.isReturnable ? t("common.yes") : t("common.no")}
        </Button>
      )
    },
  ];

  return (
    <Box sx={{ width: "100%", px: isMobile ? 2 : 5, py: isMobile ? 3 : 6 }}>
      <Box sx={{ ...centered_flex_box, flexDirection: "column", textAlign: "center", gap: 2 }}>
        <Typography sx={{ color: "var(--secColor4)", fontWeight: 700 }} fontSize={isMobile ? 22 : 28}>{t("returnsSystem.manageProducts.title")}</Typography>
      </Box>
      <NewCard content={
        <Box>
          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <MainInput
              margin="normal"
              placeholder={t("returnsSystem.manageProducts.searchPlaceholder")}
              value={search}
              onChange={({ target }) => setSearch(target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {search && (
                      <IconButton
                        onClick={handleClearSearch}
                        edge="end"
                        sx={{ color: "var(--secColor4)" }}
                      >
                        ×
                      </IconButton>
                    )}
                    <IconButton
                      onClick={handleSearch}
                      edge="end"
                      sx={{ color: "var(--secColor4)" }}
                    >
                      <Search />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: '100%', maxWidth: 400 }}
            />
          </Box>
          
          <Table
            columns={columns}
            dataSource={filteredProducts}
            loading={isLoading}
            rowKey={(r) => r.product}
            pagination={{ pageSize: 10 }}
            scroll={{ x: isMobile ? 300 : undefined }}
          />
          {productFlags.length > 0 && (
            <Box sx={{ mt: 2, ...centered_flex_box, gap: 2 }}>
              <Button sx={{ ...main_button }} onClick={handleSaveProducts}>{t("returnsSystem.manageProducts.saveRules")}</Button>
              <Button sx={{ ...main_button }} onClick={() => navigate('/returns-system')}>{t("returnsSystem.manageProducts.backToHome")}</Button>
            </Box>
          )}
        </Box>
      } />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  products: state?.records?.products,
  isLoading: state?.records?.isLoading,
});

const mapDispatchToProps = { getProductsSimplified, updateReturnsSystemProducts };

export default connect(mapStateToProps, mapDispatchToProps)(ManageProducts);